// src/ui/overlays/TeamSwitchOverlay.tsx — P3 GAME-08/09/11, UI-SPEC
// §TeamSwitchOverlay.
//
// Inline text overlay that appears during the 7-second diegetic team-switch
// window (round.phase='ended'). Picks one of 3 frozen copy variants per
// endReason deterministically via `tick % 3` (D-32).
//
// Lifecycle:
//   - Mounts when 'team-switched' event fires.
//   - Displays one variant immediately; fades in via CSS transition.
//   - Auto-fades to opacity 0 after ~3 s, then unmounts 400 ms later.
//   - Unmounts immediately on the next 'round-started' event (defensive).
//
// Source: UI-SPEC §TeamSwitchOverlay + §Copywriting Contract.

import { useEffect, useRef, useState, type JSX } from 'react';

import { renderBus } from '../../render/bus';

import './team-switch-overlay.css';

const HOLD_MS = 3000;
const FADE_OUT_MS = 400;

// UAT Test 10 follow-up: variant pools are keyed by endReason × pre-flip
// player role. Team-switch flips EVERY entity's role regardless of endReason,
// so a hider becomes a hunter and vice versa. Prior logic assumed the player
// was always a hider on `all-caught` and a hunter on `timer`, which was
// nonsense for the 50% of cases where the assumption inverted.
const VARIANTS = {
  'all-caught': {
    hider: [
      'All caught — your turn to hunt.',
      "They've got you. Now you find them.",
      'Round over. Swap roles — go hunting.',
    ],
    hunter: [
      'All caught — now your turn to hide.',
      "You've had them all. Now go vanish.",
      'Round over. Swap roles — find a spot.',
    ],
  },
  timer: {
    hider: [
      "You survived — now you hunt.",
      "Time's up. You held out. Now chase them.",
      "Round over. You're the hunter now.",
    ],
    hunter: [
      "Time's up — now your turn to hide.",
      'They held out. Now you do.',
      "Round over. Find a spot before the head-start ends.",
    ],
  },
} as const;

type EndReason = keyof typeof VARIANTS;
type PreFlipRole = 'hunter' | 'hider';

export function TeamSwitchOverlay(): JSX.Element | null {
  const [line, setLine] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  // Remember the most recent endReason seen on 'round-ended'; 'team-switched'
  // arrives a few ticks later and needs it to pick the correct variant pool.
  const lastReasonRef = useRef<EndReason | null>(null);
  // Cache the most recent player role from sim-tick. The team-switch window
  // holds inTeamswitchTransition=true but roles don't flip until
  // flipRolesAndReset fires at the end of the window, so sim-tick still
  // reports the PRE-flip role during the overlay's lifetime — exactly what
  // we want.
  const preFlipRoleRef = useRef<PreFlipRole>('hider');

  useEffect(() => {
    const offTick = renderBus.on('sim-tick', (e) => {
      if (e.playerRole === 'hunter' || e.playerRole === 'hider') {
        preFlipRoleRef.current = e.playerRole;
      }
    });
    const offEnded = renderBus.on('round-ended', (e) => {
      lastReasonRef.current = e.endReason;
    });
    const offSwitched = renderBus.on('team-switched', (e) => {
      const reason: EndReason = lastReasonRef.current ?? 'all-caught';
      const preRole = preFlipRoleRef.current;
      const pool = VARIANTS[reason][preRole];
      const idx = Math.abs(e.tick) % pool.length;
      setLine(pool[idx]!);
      setVisible(true);
      // Auto-fade after HOLD_MS; unmount after the fade completes.
      window.setTimeout(() => setVisible(false), HOLD_MS);
      window.setTimeout(() => setLine(null), HOLD_MS + FADE_OUT_MS);
    });
    const offStarted = renderBus.on('round-started', () => {
      setLine(null);
      setVisible(false);
    });
    return () => {
      offTick();
      offEnded();
      offSwitched();
      offStarted();
    };
  }, []);

  if (!line) return null;

  // Split " — " so the trailing clause renders with the accent amber colour
  // (UI-SPEC §Color, §Copywriting). Each frozen variant either has an em-dash
  // break ("All caught — your turn to hunt.") or is single-clause.
  const hasAccent = line.includes(' — ');
  const [lead, accent] = hasAccent ? line.split(' — ') : [line, ''];

  return (
    <div
      className="team-switch-overlay"
      data-testid="team-switch-overlay"
      data-visible={visible ? 'true' : 'false'}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {lead}
      {hasAccent && (
        <>
          {' — '}
          <span className="accent">{accent}</span>
        </>
      )}
    </div>
  );
}
